// src/pages/customers/CustomerEditPage.tsx
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Loader2,
  Save,
  User,
  Wifi,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../components/ui/Toast";
import { API_CONFIG, fetchApp } from "../../services/apiService";

interface Plan {
  id: string;
  name: string;
  mikrotik_profile_id: string;
  local_address?: string | null;
  remote_pool?: string | null;
  speed: string;
  dns_servers?: string | null;
  description?: string | null;
  price: number;
  created_at: string;
  updated_at: string;
}

interface SecretDetail {
  id: string;
  username: string;
  email: string;
  phone_number: string;
  status: boolean;
  account_number: string;
  amount_payable?: number | null;
  plan_id: string | null;
  password?: string;
  router_id: string;
  created_at: string;
  updated_at: string;
  service_expires_at?: string | null;
}

const toDatetimeLocalValue = (value?: string | null) => {
  if (!value) return "";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  return d.toISOString().slice(0, 16);
};

const formatKES = (value: number) =>
  `KES ${new Intl.NumberFormat("en-KE").format(value)}`;

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  description?: string;
  tone?: "emerald" | "sky" | "amber" | "indigo" | "slate";
}> = ({ icon, title, description, tone = "slate" }) => {
  const tones = {
    emerald: "bg-emerald-100 text-emerald-700",
    sky: "bg-sky-100 text-sky-700",
    amber: "bg-amber-100 text-amber-700",
    indigo: "bg-indigo-100 text-indigo-700",
    slate: "bg-slate-200 text-slate-600",
  };

  return (
    <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-100/80 px-5 py-4">
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${tones[tone]}`}
      >
        {icon}
      </div>

      <div className="min-w-0">
        <p className="text-[13.5px] font-black text-slate-800">{title}</p>

        {description && (
          <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
            {description}
          </p>
        )}
      </div>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  hint?: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
  span2?: boolean;
}> = ({ label, hint, required, children, span2 }) => (
  <div
    className={["flex flex-col gap-1.5", span2 ? "sm:col-span-2" : ""]
      .filter(Boolean)
      .join(" ")}
  >
    <label className="flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-slate-400">
      {label}
      {required && <span className="text-rose-500">*</span>}
    </label>

    {children}

    {hint && <div className="mt-0.5">{hint}</div>}
  </div>
);

const Hint: React.FC<{
  children: React.ReactNode;
  variant?: "default" | "warn";
}> = ({ children, variant = "default" }) => (
  <p
    className={`flex items-start gap-1.5 text-[11.5px] font-medium leading-snug ${
      variant === "warn" ? "text-amber-700" : "text-slate-500"
    }`}
  >
    {variant === "warn" && (
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
    )}

    {children}
  </p>
);

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13.5px] font-medium text-slate-800 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const CustomerEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [secret, setSecret] = useState<SecretDetail | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [planId, setPlanId] = useState("");
  const [status, setStatus] = useState(true);
  const [accountNumber, setAccountNumber] = useState("");
  const [amountPayable, setAmountPayable] = useState("");
  const [password, setPassword] = useState("");
  const [serviceExpiry, setServiceExpiry] = useState("");

  const loadData = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const detail: SecretDetail = await fetchApp(
        `${API_CONFIG.baseUrl}/customers/${id}`,
      );

      setSecret({
        ...detail,
        id: String(detail.id),
        account_number: String(detail.account_number ?? ""),
        amount_payable:
          detail.amount_payable === null || detail.amount_payable === undefined
            ? null
            : Number(detail.amount_payable),
        plan_id: detail.plan_id ? String(detail.plan_id) : null,
        router_id: String(detail.router_id ?? ""),
      });

      setUsername(detail.username || "");
      setEmail(detail.email || "");
      setPhoneNumber(detail.phone_number || "");
      setPlanId(detail.plan_id ? String(detail.plan_id) : "");
      setStatus(Boolean(detail.status));
      setAccountNumber(String(detail.account_number ?? ""));
      setAmountPayable(
        detail.amount_payable === null || detail.amount_payable === undefined
          ? ""
          : String(detail.amount_payable),
      );
      setPassword(detail.password || "");
      setServiceExpiry(toDatetimeLocalValue(detail.service_expires_at));

      const plansRes: Plan[] = await fetchApp(`${API_CONFIG.baseUrl}/plans`);

      setPlans(
        plansRes.map((p) => ({
          ...p,
          id: String(p.id),
          mikrotik_profile_id: String(p.mikrotik_profile_id ?? ""),
        })),
      );
    } catch (e: any) {
      setError(e?.message || "Failed to load customer for editing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const getApiErrorMessage = (error: any): string => {
    const fallback =
      "Failed to save changes. Please check the details and try again.";

    const data = error?.response?.data || error?.data || error;

    if (typeof data === "string") {
      return data;
    }

    if (typeof data?.message === "string") {
      return data.message;
    }

    if (typeof data?.error === "string") {
      return data.error;
    }

    if (typeof data?.detail === "string") {
      return data.detail;
    }

    if (Array.isArray(data?.detail)) {
      return data.detail
        .map((item: any) => {
          const field = Array.isArray(item.loc)
            ? item.loc.filter((x: string) => x !== "body").join(".")
            : "";

          const message = item.msg || "Invalid value";

          return field ? `${field}: ${message}` : message;
        })
        .join("\n");
    }

    if (error?.message && error.message !== "[object Object]") {
      return error.message;
    }

    return fallback;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!secret) return;

    const parsedAmountPayable = Number(amountPayable);

    if (!amountPayable.trim() || Number.isNaN(parsedAmountPayable)) {
      const msg = "Amount payable is required.";
      setError(msg);
      showToast(msg, "error");
      return;
    }

    if (parsedAmountPayable <= 0) {
      const msg = "Amount payable must be greater than 0.";
      setError(msg);
      showToast(msg, "error");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const body = {
        username,
        email,
        phone_number: phoneNumber,
        plan_id: planId === "" ? null : planId,
        amount_payable: parsedAmountPayable,
        status,
        account_number: accountNumber ? String(accountNumber) : null,
        password: password || undefined,
        service_expires_at: serviceExpiry
          ? new Date(serviceExpiry).toISOString()
          : null,
      };

      const res = await fetchApp(
        `${API_CONFIG.baseUrl}/secrets/${secret.id}/update`,
        {
          method: "PUT",
          body: JSON.stringify(body),
        },
      );

      if (res && ((res as any).error || (res as any).detail)) {
        throw res;
      }

      showToast("Customer updated successfully!", "success");
      navigate(`/customers/${secret.id}`);
    } catch (e: any) {
      const msg = getApiErrorMessage(e);

      setError(msg);
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };
  const handleCancel = () =>
    secret ? navigate(`/customers/${secret.id}`) : navigate("/customers");

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#eef3f8]">
        <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
          <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
        </div>

        <p className="text-[13px] font-bold text-slate-500">
          Loading customer data…
        </p>
      </div>
    );
  }

  if (!secret) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#eef3f8] px-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-amber-200 bg-amber-50 shadow-lg shadow-amber-200/40">
          <AlertTriangle className="h-7 w-7 text-amber-500" />
        </div>

        <div>
          <p className="text-[15px] font-black text-slate-800">
            Customer Not Found
          </p>

          <p className="mx-auto mt-1 max-w-xs text-[13px] font-medium text-slate-500">
            The customer you are trying to edit does not exist or has been
            removed.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/customers")}
          className="inline-flex items-center gap-1.5 rounded-2xl bg-emerald-500 px-4 py-2 text-[13px] font-bold text-white shadow-lg shadow-emerald-500/20 transition-all hover:bg-emerald-600 active:scale-[0.98]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Customers
        </button>
      </div>
    );
  }

  const selectedPlan = plans.find((p) => p.id === planId) ?? null;

  return (
    <div className="min-h-screen bg-[#eef3f8] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1400px] space-y-6">
        <button
          type="button"
          onClick={handleCancel}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-50 px-3 py-1.5 text-[12.5px] font-bold text-slate-500 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Profile
        </button>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.08em] text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Customer Profile Update
                </div>

                <h1 className="mt-3 text-[24px] font-black tracking-[-0.5px] text-slate-900">
                  Edit Customer
                </h1>

                <p className="mt-1 max-w-2xl text-[13.5px] font-medium text-slate-500">
                  Update contact information, PPPoE credentials, billing amount,
                  plan assignment, and service expiry.
                </p>
              </div>

              <div
                className={`rounded-3xl border px-4 py-3 shadow-sm ${
                  status
                    ? "border-emerald-200 bg-emerald-50/90 shadow-emerald-200/40"
                    : "border-slate-300 bg-slate-100/90 shadow-slate-300/40"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl ${
                      status
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-200 text-slate-600"
                    }`}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </div>

                  <div>
                    <p
                      className={`text-[12.5px] font-black ${
                        status ? "text-emerald-800" : "text-slate-700"
                      }`}
                    >
                      {status ? "Active Customer" : "Inactive Customer"}
                    </p>

                    <p
                      className={`text-[11.5px] font-medium ${
                        status ? "text-emerald-700" : "text-slate-500"
                      }`}
                    >
                      Current account number: {accountNumber || "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
            <div className="space-y-6">
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-lg shadow-slate-300/25">
                <SectionHeader
                  icon={<User className="h-4 w-4" />}
                  tone="sky"
                  title="User Details"
                  description="Basic profile and communication details"
                />

                <div className="p-5 sm:p-6">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Full Name" required>
                      <input
                        type="text"
                        className={inputCls}
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="e.g. John Kamau"
                        required
                      />
                    </Field>

                    <Field
                      label="Phone Number"
                      hint={
                        <Hint>Used for automatic payment notifications.</Hint>
                      }
                    >
                      <input
                        type="tel"
                        className={inputCls}
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="2547…"
                      />
                    </Field>

                    <Field label="Email Address" span2>
                      <input
                        type="email"
                        className={inputCls}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="john@example.com"
                      />
                    </Field>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-lg shadow-slate-300/25">
                <SectionHeader
                  icon={<Calendar className="h-4 w-4" />}
                  tone="indigo"
                  title="Service Expiry"
                  description="Manage billing cycle anchors and expiration dates"
                />

                <div className="p-5 sm:p-6">
                  <Field
                    label="Service Expiry Date"
                    hint={
                      <Hint>
                        Manually overriding this changes the billing cycle
                        anchor. Clear to let the system auto-calculate.
                      </Hint>
                    }
                  >
                    <input
                      type="datetime-local"
                      className={inputCls}
                      value={serviceExpiry}
                      onChange={(e) => setServiceExpiry(e.target.value)}
                    />
                  </Field>

                  <div className="mt-4 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3">
                    <Hint>
                      Leave blank if the expiry should be controlled
                      automatically by the system.
                    </Hint>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-lg shadow-slate-300/25">
                <SectionHeader
                  icon={<Wifi className="h-4 w-4" />}
                  tone="emerald"
                  title="PPPoE & Subscription"
                  description="Network credentials, account status, and internet plan"
                />

                <div className="p-5 sm:p-6">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field
                      label="PPPoE Username / Account Number"
                      hint={
                        <Hint variant="warn">
                          Changing this may disconnect the user immediately.
                        </Hint>
                      }
                    >
                      <input
                        type="text"
                        className={inputCls}
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value)}
                      />
                    </Field>

                    <Field
                      label="PPPoE Password"
                      hint={
                        <Hint>Leave blank to keep the current password.</Hint>
                      }
                    >
                      <input
                        type="text"
                        className={inputCls}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••"
                        autoComplete="off"
                      />
                    </Field>

                    <Field label="Account Status" span2>
                      <div className="grid grid-cols-2 gap-2.5 pt-0.5">
                        <button
                          type="button"
                          onClick={() => setStatus(true)}
                          className={`flex items-center justify-center gap-2 rounded-2xl border py-2.5 text-[13px] font-black transition-all ${
                            status
                              ? "border-emerald-200 bg-emerald-100 text-emerald-800 shadow-sm shadow-emerald-200/50"
                              : "border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100"
                          }`}
                        >
                          <span
                            className={`h-2 w-2 rounded-full ${
                              status ? "bg-emerald-500" : "bg-slate-300"
                            }`}
                          />
                          Active
                        </button>

                        <button
                          type="button"
                          onClick={() => setStatus(false)}
                          className={`flex items-center justify-center gap-2 rounded-2xl border py-2.5 text-[13px] font-black transition-all ${
                            !status
                              ? "border-slate-300 bg-slate-200 text-slate-700 shadow-sm"
                              : "border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100"
                          }`}
                        >
                          <span
                            className={`h-2 w-2 rounded-full ${
                              !status ? "bg-slate-600" : "bg-slate-300"
                            }`}
                          />
                          Inactive
                        </button>
                      </div>
                    </Field>

                    <Field
                      label="Internet Plan"
                      span2
                      hint={
                        <Hint>
                          Updates the MikroTik user profile immediately after
                          saving.
                        </Hint>
                      }
                    >
                      <div className="relative">
                        <select
                          className={`${inputCls} cursor-pointer appearance-none pr-9`}
                          value={planId}
                          onChange={(e) => setPlanId(e.target.value)}
                        >
                          <option value="">— No Plan Selected —</option>

                          {plans.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} ({p.speed}) —{" "}
                              {formatKES(Number(p.price || 0))}
                            </option>
                          ))}
                        </select>

                        <svg
                          className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>

                      {selectedPlan && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1.5 text-[11.5px] font-black text-emerald-800">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {selectedPlan.name}
                          </span>

                          {selectedPlan.speed && (
                            <span className="rounded-full border border-sky-200 bg-sky-100 px-3 py-1.5 text-[11.5px] font-black text-sky-800">
                              {selectedPlan.speed}
                            </span>
                          )}

                          <span className="rounded-full border border-slate-300 bg-slate-200 px-3 py-1.5 text-[11.5px] font-bold text-slate-600">
                            {formatKES(Number(selectedPlan.price || 0))} / cycle
                          </span>
                        </div>
                      )}
                    </Field>

                    <Field
                      label="Amount Payable"
                      required
                      span2
                      hint={
                        <Hint>
                          Billing uses this value as the source of truth, not
                          the plan price.
                        </Hint>
                      }
                    >
                      <input
                        type="number"
                        min="1"
                        step="1"
                        className={inputCls}
                        value={amountPayable}
                        onChange={(e) => setAmountPayable(e.target.value)}
                        placeholder="e.g. 1500"
                        required
                      />
                    </Field>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 z-10 -mx-4 border-t border-slate-200 bg-[#eef3f8]/90 px-4 py-4 backdrop-blur-xl sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            <div className="mx-auto flex max-w-[1400px] flex-col-reverse gap-2.5 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-5 py-2.5 text-[13.5px] font-black text-slate-600 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-7 py-2.5 text-[13.5px] font-black text-white shadow-lg shadow-emerald-500/20 transition-all hover:from-emerald-600 hover:to-teal-600 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}

                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerEditPage;
